import { Box, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { InfoButton } from 'components';
import React, { useCallback } from 'react';
import { texts } from 'utils/languages/fr';
import {
    MemoizedCountriesSelect,
    MemoizedIndexedButtonsActions,
    MemoizedIndexedCountriesSelect,
    MemoizedIndexedTextField,
    MemoizedRadioGroup,
    MemoizedTextField,
} from '../memoized-components/MemoizedComponents';

const useStyles = makeStyles(theme => ({
    field: {
        width: '100%',
        maxWidth: theme.constants.tunnelInputWidth,
        '&$fiscalObligationField': {
            marginRight: 10,
        },
    },
    fiscalObligationField: {},
    fiscalObligationLine: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-end',
        [theme.breakpoints.down(600)]: {
            flexDirection: 'column',
            border: `1px solid ${theme.palette.grey['A700']}`,
            padding: 10,
            alignItems: 'flex-start',
            marginBottom: 10,
        },
    },
    actionButtons: {
        alignSelf: 'flex-end',
        marginBottom: '16px',
        [theme.breakpoints.down(600)]: {
            alignSelf: 'flex-start',
        },
    },
    infoButton: { marginTop: 10 },
    label: {
        color: 'rgba(0, 0, 0, 0.54)',
        letterSpacing: 1.6,
    },
}));

const FiscalSituationFormFCPRPP = ({
    errors,
    fiscalSituation,
    fiscalObligations,
    onChange,
    onAddLine,
    onRemoveLine,
    onUpdateLine,
}) => {
    const classes = useStyles();

    const handleChange = useCallback(
        e => {
            const name = e.target.name;
            const value = e.target.value;
            onChange(name, value);
        },
        [onChange]
    );

    const renderFiscalObligationLine = (fo, index, arr) => {
        return (
            <div key={index} className={classes.fiscalObligationLine}>
                <MemoizedIndexedCountriesSelect
                    className={clsx(classes.field, classes.fiscalObligationField)}
                    name="country"
                    value={fo.country}
                    index={index}
                    onChange={onUpdateLine}
                />
                <MemoizedIndexedTextField
                    className={clsx(classes.field, classes.fiscalObligationField)}
                    name="nif"
                    label={texts.NIF}
                    value={fo.nif}
                    index={index}
                    onChange={onUpdateLine}
                />
                <MemoizedIndexedTextField
                    className={clsx(classes.field, classes.fiscalObligationField)}
                    name="nifReason"
                    label={texts.NIF_ABSENCE_REASON}
                    value={fo.nifReason}
                    index={index}
                    onChange={onUpdateLine}
                />
                <MemoizedIndexedButtonsActions
                    className={classes.actionButtons}
                    index={index}
                    onRemove={onRemoveLine}
                    onAdd={onAddLine}
                    isLast={index === arr.length - 1}
                />
            </div>
        );
    };

    if (!fiscalSituation) {
        return null;
    }

    return (
        <React.Fragment>
            <Box display="flex" alignItems="center">
                <Typography className={classes.label}>{texts.COCHER_UNE_DES_DEUX_CASES}</Typography>
                <InfoButton
                    disableMarginBottom
                    title={texts.US_PERSON}
                    content={
                        <React.Fragment>
                            <Typography gutterBottom>
                                Une <i>US person</i> est soit :
                            </Typography>
                            <ul>
                                <li>
                                    <Typography gutterBottom>
                                        {texts.US_PERSON_TEXT_OPT1}
                                    </Typography>
                                </li>
                                <li>
                                    <Typography gutterBottom>
                                        {texts.US_PERSON_TEXT_OPT2}
                                    </Typography>
                                </li>
                            </ul>
                        </React.Fragment>
                    }
                />
            </Box>
            <MemoizedRadioGroup
                name="isUSPerson"
                value={fiscalSituation.isUSPerson}
                onChange={handleChange}
                yesValue="ineligible"
                noValue="eligible"
                yesLabel={texts.JE_SUIS_US_PERSON}
                noLabel={texts.JE_SUIS_NON_US_PERSON}
            />

            <Typography className={classes.label} gutterBottom>
                {texts.RESIDENT_FISCAL_USA}
            </Typography>
            <MemoizedRadioGroup
                name="wasUSFiscalResident"
                value={fiscalSituation.wasUSFiscalResident}
                onChange={handleChange}
                yesValue="true"
                noValue="false"
                yesLabel={texts.OUI}
                noLabel={texts.NON}
            />
            {fiscalSituation.wasUSFiscalResident === 'true' && (
                <MemoizedTextField
                    name="tin"
                    inputStyle={classes.field}
                    label={texts.ASK_TIN_NUMBER}
                    value={fiscalSituation.tin}
                    onChange={handleChange}
                />
            )}

            <MemoizedCountriesSelect
                inputStyle={classes.field}
                name="taxResident"
                label={texts.FISCAL_RESIDENCE}
                value={fiscalSituation.taxResident}
                onChange={handleChange}
                error={errors.taxResident}
            />

            <Typography className={classes.label} gutterBottom>
                {texts.FISCAL_OBLIGATION_OTHER_COUNTRY}
            </Typography>
            <MemoizedRadioGroup
                name="fiscalObligationOtherCountry"
                value={fiscalSituation.fiscalObligationOtherCountry}
                onChange={handleChange}
                yesValue="true"
                noValue="false"
                yesLabel={texts.OUI}
                noLabel={texts.NON}
            />
            {fiscalSituation.fiscalObligationOtherCountry === 'true' && (
                <>
                    <Typography gutterBottom>{texts.FISCAL_OBLIGATION_QUESTION}</Typography>
                    {fiscalObligations.map(renderFiscalObligationLine)}
                </>
            )}
        </React.Fragment>
    );
};

export default FiscalSituationFormFCPRPP;
